import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL = (!RAW_BACKEND_URL || RAW_BACKEND_URL === 'undefined') ? '' : RAW_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Handles the return leg of the Emergent Google OAuth redirect. Rendered by
 * AppRoutes synchronously whenever the URL fragment contains `session_id=`,
 * BEFORE the normal routes/guards run (prevents race conditions).
 */
export const GoogleAuthCallback = () => {
  const navigate = useNavigate();
  const { applySession } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    (async () => {
      // session_id lives in the URL fragment: #session_id=...
      const hash = window.location.hash || '';
      const match = hash.match(/session_id=([^&]+)/);
      const sessionId = match ? decodeURIComponent(match[1]) : null;
      // Strip the fragment so a refresh doesn't re-trigger the exchange.
      try { window.history.replaceState(null, '', window.location.pathname + window.location.search); } catch (_) { /* noop */ }

      if (!sessionId) {
        setError('Missing Google session. Please try signing in again.');
        return;
      }

      try {
        const { data } = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
        applySession(data.access_token, data.user);

        // Resume a pending SSO authorization if the user started from /sso/login.
        let sso = null;
        try { sso = JSON.parse(sessionStorage.getItem('sso_pending') || 'null'); } catch (_) { sso = null; }
        if (sso?.client_id && sso?.redirect_uri) {
          try {
            const resp = await axios.post(
              `${API}/sso/authorize/consent`,
              {
                client_id: sso.client_id,
                redirect_uri: sso.redirect_uri,
                scope: sso.scope || 'openid profile email',
                state: sso.state || null,
                code_challenge: sso.code_challenge || null,
                code_challenge_method: sso.code_challenge_method || null,
              },
              { headers: { Authorization: `Bearer ${data.access_token}` } },
            );
            sessionStorage.removeItem('sso_pending');
            window.location.href = resp.data.redirect_url;
            return;
          } catch (e) {
            sessionStorage.removeItem('sso_pending');
            // Fall through to dashboard if consent fails (e.g. app disabled).
          }
        }
        navigate('/dashboard', { replace: true });
      } catch (e) {
        setError(e.response?.data?.detail || 'Google sign-in failed. Please try again.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4" data-testid="google-callback-error">
        <div className="text-center space-y-3 max-w-sm">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto">
            <AlertCircle className="w-6 h-6 text-red-500" />
          </div>
          <p className="text-zinc-700 font-medium">Couldn't complete sign-in</p>
          <p className="text-sm text-zinc-500">{error}</p>
          <Button onClick={() => navigate('/login', { replace: true })} data-testid="google-callback-retry">
            Back to sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center" data-testid="google-callback-loading">
      <div className="text-center space-y-3">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto" />
        <p className="text-sm text-zinc-500">Finishing sign-in…</p>
      </div>
    </div>
  );
};
